import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const apiCall = async (endpoint: string, options?: RequestInit) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No auth token');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

interface BetaUser {
  email: string;
  approved: boolean;
  addedAt: string;
  addedBy: string;
  notes: string;
  signedUp: boolean;
  signedUpAt: string | null;
}

interface WaitlistEntry {
  email: string;
  name: string;
  businessType: string;
  message: string;
  addedAt: string;
  notified: boolean;
}

interface WaitlistConfig {
  config: {
    maxFreeBetaUsers: number;
    maxPaidUsers: number;
    waitlistEnabled: boolean;
    currentBetaCount: number;
    approvedNotSignedUpCount: number;
    currentPaidCount: number;
    waitlistCount: number;
    betaUserEmails: BetaUser[];
    waitlist: WaitlistEntry[];
    lastUpdated: string;
    updatedBy: string;
  };
  stats: {
    betaSpotsRemaining: number;
    paidSpotsRemaining: number;
    totalSpotsRemaining: number;
  };
}

const LaunchControl: React.FC = () => {
  const queryClient = useQueryClient();
  const [newBetaEmail, setNewBetaEmail] = useState('');
  const [newBetaNotes, setNewBetaNotes] = useState('');
  const [editingLimits, setEditingLimits] = useState(false);
  const [maxBeta, setMaxBeta] = useState(5);
  const [maxPaid, setMaxPaid] = useState(5);

  // Fetch waitlist config
  const { data, isLoading, error } = useQuery<WaitlistConfig>({
    queryKey: ['waitlist-config'],
    queryFn: () => apiCall('/api/admin-frontend/waitlist-config'),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Approve beta user mutation
  const approveBetaMutation = useMutation({
    mutationFn: (data: { email: string; notes: string }) =>
      apiCall('/api/admin-frontend/approve-beta-user', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-config'] });
      setNewBetaEmail('');
      setNewBetaNotes('');
    }
  });

  // Remove beta user mutation
  const removeBetaMutation = useMutation({
    mutationFn: (email: string) =>
      apiCall(`/api/admin-frontend/remove-beta-user/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-config'] });
    }
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: (config: { maxFreeBetaUsers?: number; maxPaidUsers?: number; waitlistEnabled?: boolean }) =>
      apiCall('/api/admin-frontend/waitlist-config', {
        method: 'PUT',
        body: JSON.stringify(config)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-config'] });
      setEditingLimits(false);
    }
  });

  // Promote waitlist to beta mutation
  const promoteToBetaMutation = useMutation({
    mutationFn: (email: string) =>
      apiCall(`/api/admin-frontend/promote-waitlist-to-beta/${encodeURIComponent(email)}`, {
        method: 'POST',
        body: JSON.stringify({})
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-config'] });
    }
  });

  // Remove from waitlist mutation
  const removeFromWaitlistMutation = useMutation({
    mutationFn: (email: string) =>
      apiCall(`/api/admin-frontend/remove-from-waitlist/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-config'] });
    }
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-200 dark:bg-gray-700 h-32 rounded-xl"></div>
          ))}
        </div>
        <div className="bg-gray-200 dark:bg-gray-700 h-64 rounded-xl"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <p className="text-red-800 dark:text-red-200">
          Failed to load launch control data. Please try again.
        </p>
      </div>
    );
  }

  const { config, stats } = data;

  const handleApproveBeta = (e: React.FormEvent) => {
    e.preventDefault();
    if (newBetaEmail.trim()) {
      approveBetaMutation.mutate({ email: newBetaEmail.trim(), notes: newBetaNotes.trim() });
    }
  };

  const handleUpdateLimits = () => {
    updateConfigMutation.mutate({
      maxFreeBetaUsers: maxBeta,
      maxPaidUsers: maxPaid
    });
  };

  const handleToggleWaitlist = () => {
    updateConfigMutation.mutate({
      waitlistEnabled: !config.waitlistEnabled
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Launch Control
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Manage your beta launch and waitlist • Last updated: {new Date(config.lastUpdated).toLocaleString()}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Beta Users Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Free Beta Users</h3>
            <span className="px-2 py-1 text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
              FREE
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.currentBetaCount}
            </span>
            <span className="text-lg text-gray-500 dark:text-gray-400">
              / {config.maxFreeBetaUsers}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
            <div
              className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all"
              style={{ width: `${(config.currentBetaCount / config.maxFreeBetaUsers) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {stats.betaSpotsRemaining} spot{stats.betaSpotsRemaining !== 1 ? 's' : ''} remaining
          </p>
          {config.approvedNotSignedUpCount > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              ⏳ {config.approvedNotSignedUpCount} approved, not signed up yet
            </p>
          )}
        </div>

        {/* Paid Users Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Paid Users</h3>
            <span className="px-2 py-1 text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
              PAID
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.currentPaidCount}
            </span>
            <span className="text-lg text-gray-500 dark:text-gray-400">
              / {config.maxPaidUsers}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
            <div
              className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(config.currentPaidCount / config.maxPaidUsers) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {stats.paidSpotsRemaining} spot{stats.paidSpotsRemaining !== 1 ? 's' : ''} remaining
          </p>
        </div>

        {/* Waitlist Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Waitlist</h3>
            <button
              onClick={handleToggleWaitlist}
              className={`px-2 py-1 text-xs font-bold rounded-full transition-colors ${
                config.waitlistEnabled
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
              }`}
            >
              {config.waitlistEnabled ? 'ENABLED' : 'DISABLED'}
            </button>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.waitlistCount}
            </span>
            <span className="text-lg text-gray-500 dark:text-gray-400">waiting</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            Total capacity: {config.maxFreeBetaUsers + config.maxPaidUsers} users
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {stats.totalSpotsRemaining} total spot{stats.totalSpotsRemaining !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>

      {/* Settings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h3>
          <button
            onClick={() => {
              if (editingLimits) {
                setMaxBeta(config.maxFreeBetaUsers);
                setMaxPaid(config.maxPaidUsers);
              } else {
                setMaxBeta(config.maxFreeBetaUsers);
                setMaxPaid(config.maxPaidUsers);
              }
              setEditingLimits(!editingLimits);
            }}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            {editingLimits ? 'Cancel' : 'Edit Limits'}
          </button>
        </div>

        {editingLimits ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Beta Users
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxBeta}
                  onChange={(e) => setMaxBeta(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Paid Users
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxPaid}
                  onChange={(e) => setMaxPaid(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <button
              onClick={handleUpdateLimits}
              disabled={updateConfigMutation.isPending}
              className="w-full px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
            >
              {updateConfigMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Max Beta Users:</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">{config.maxFreeBetaUsers}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Max Paid Users:</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">{config.maxPaidUsers}</span>
            </div>
          </div>
        )}
      </div>

      {/* Approve Beta User Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Approve Beta User</h3>
        <form onSubmit={handleApproveBeta} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={newBetaEmail}
              onChange={(e) => setNewBetaEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (optional)
            </label>
            <input
              type="text"
              value={newBetaNotes}
              onChange={(e) => setNewBetaNotes(e.target.value)}
              placeholder="e.g., Referred by John, early supporter..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <button
            type="submit"
            disabled={approveBetaMutation.isPending || stats.betaSpotsRemaining === 0}
            className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {approveBetaMutation.isPending ? 'Approving...' : stats.betaSpotsRemaining === 0 ? 'Beta Full - Increase Limit First' : 'Approve for Free Beta Access'}
          </button>
          {approveBetaMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {approveBetaMutation.error instanceof Error ? approveBetaMutation.error.message : 'Failed to approve user'}
            </p>
          )}
        </form>
      </div>
      {/* Beta Users List */}
      {config.betaUserEmails.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Approved Beta Users ({config.betaUserEmails.length})
          </h3>
          <div className="space-y-2">
            {config.betaUserEmails.map((betaUser) => (
              <div
                key={betaUser.email}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {betaUser.email}
                    </span>
                    {betaUser.signedUp ? (
                      <span className="px-2 py-0.5 text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                        ✓ SIGNED UP
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                        ⏳ PENDING
                      </span>
                    )}
                  </div>
                  {betaUser.notes && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{betaUser.notes}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Added {new Date(betaUser.addedAt).toLocaleDateString()} by {betaUser.addedBy}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`Remove ${betaUser.email} from beta access?`)) {
                      removeBetaMutation.mutate(betaUser.email);
                    }
                  }}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waitlist */}
      {config.waitlist.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Waitlist ({config.waitlist.length})
          </h3>
          <div className="space-y-2">
            {config.waitlist.map((entry, index) => (
              <div
                key={entry.email}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs font-bold bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                      #{index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {entry.email}
                    </span>
                    {entry.name && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        ({entry.name})
                      </span>
                    )}
                  </div>
                  {entry.businessType && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Business: {entry.businessType}
                    </p>
                  )}
                  {entry.message && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      "{entry.message}"
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Joined {new Date(entry.addedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (window.confirm(`Promote ${entry.email} to beta access?`)) {
                        promoteToBetaMutation.mutate(entry.email);
                      }
                    }}
                    disabled={stats.betaSpotsRemaining === 0}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Promote to Beta
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${entry.email} from waitlist?`)) {
                        removeFromWaitlistMutation.mutate(entry.email);
                      }
                    }}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LaunchControl;
